import clsx from "clsx";
import deburr from "lodash/deburr";
import groupBy from "lodash/groupBy";
import React, { useEffect, useRef, useState } from "react";
import CheckboxFilterItem from "web/react/components/checkbox-filter-item/checkbox-filter-item";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import browser from "web/script/utils/browser";
import { StandardFilterProps } from "web/types/feed-filters";
import { FilteredScrollAreaFilterOptionGroup } from "web/types/universal-filters";
import { FilterAlphabetisedSection } from "./filter-alphabetised-section";
import styles from "./filter-option-group.module.css";

interface FilterOptionGroupProps extends StandardFilterProps {
    isVisible: boolean;
    optionGroup: FilteredScrollAreaFilterOptionGroup;
    lazyFacetFetch: (optionGroupId: string) => void;
    lazyFacetIsLoading: boolean;
}

function FilterOptionGroup({
    isVisible,
    onClick,
    optionGroup,
    pendingFilterInfo,
    lazyFacetFetch,
    lazyFacetIsLoading,
}: FilterOptionGroupProps): JSX.Element {
    const [searchInputFocused, setSearchInputFocused] = React.useState<boolean>(false);
    const ref = useRef<HTMLDivElement>(null);

    const shouldLoadOptions = optionGroup.lazy_loaded && isVisible;
    useEffect(() => {
        if (shouldLoadOptions) {
            // rothko caching ensures we don't duplicate calls, e.g. if a filter is
            // added and then removed again
            lazyFacetFetch(optionGroup.id);
        }
    }, [lazyFacetFetch, shouldLoadOptions, optionGroup.id]);

    let [query, setQuery] = useState("");
    let toRender = optionGroup.options.filter(
        (option) => query == "" || deburr(option.label.toLowerCase()).includes(deburr(query))
    );

    function handleFilter(event: React.ChangeEvent<HTMLInputElement>): void {
        setQuery(event.target.value.toLowerCase());
    }

    function onBlurSearchInput(): void {
        setSearchInputFocused(false);
    }

    function onFocusSearchInput(): void {
        setSearchInputFocused(true);
        const isMobile = browser.getViewport() === browser.VIEWPORTS.MOBILE;

        // On iOS, the keyboard hides the autocomplete results.
        // As a fix, on search input focus, we are scrolling the search input
        // to the very top of the available Drawer scrolling space.
        if (isMobile) {
            ref?.current?.scrollIntoView({
                behavior: "smooth",
            });
        }
    }

    const REGEX_AZ = /([A-Z])/i;
    const REGEX_NUMBER = /([0-9])/;

    function getFirstCharacter(brandName): string {
        const firstCharacter = brandName.label.charAt(0).normalize("NFD")[0].toUpperCase();
        if (REGEX_AZ.test(firstCharacter)) {
            return firstCharacter;
        } else if (REGEX_NUMBER.test(firstCharacter)) {
            return "0-9";
        }
        return "!#";
    }

    const groupedBy = Object.entries(groupBy(optionGroup.options, getFirstCharacter));

    let { id, search_placeholder: placeholder } = optionGroup;

    return (
        <div
            className={clsx(styles.background, {
                [styles.loading]: lazyFacetIsLoading,
            })}
            ref={ref}
        >
            <div
                className={clsx(styles.searchField, {
                    [styles.searchFieldFocused]: searchInputFocused,
                })}
            >
                <input
                    className={styles.input}
                    type="text"
                    onChange={handleFilter}
                    onBlur={onBlurSearchInput}
                    onFocus={onFocusSearchInput}
                    placeholder={placeholder}
                    aria-label={placeholder}
                    aria-controls={`search-result_${id}`}
                />
                <div className={styles.searchIcon}>
                    <SVGIcon name="search" />
                </div>
            </div>
            <ol
                className={styles.results}
                role="region"
                id={`search-result_${id}`}
                aria-live="polite"
                aria-atomic="false"
            >
                {query !== "" ? (
                    <>
                        {toRender.map((item) => (
                            <li id={`${id}-${item.value}`} key={`${id}-${item.value}`}>
                                <CheckboxFilterItem
                                    query={query}
                                    {...{ item, onClick, pendingFilterInfo }}
                                    isSmallItem={true}
                                />
                            </li>
                        ))}
                    </>
                ) : (
                    <>
                        {groupedBy.map(([firstLetter, options]) => (
                            <FilterAlphabetisedSection key={firstLetter} title={firstLetter}>
                                {options.map((item) => (
                                    <li key={item.label} id={`${id}-${item.value}`}>
                                        <CheckboxFilterItem
                                            query={query}
                                            {...{ item, onClick, pendingFilterInfo }}
                                            isSmallItem={true}
                                        />
                                    </li>
                                ))}
                            </FilterAlphabetisedSection>
                        ))}
                    </>
                )}
            </ol>
            {lazyFacetIsLoading && <LoadingSpinner />}
        </div>
    );
}

export default FilterOptionGroup;
